import React, { useEffect, useRef, useState } from 'react'

import SafetySystemsModal from './SafetySystemsModal'
import SensorInsights, { StatusBadge, displaySensorName, formatPrice } from './adasInsights'
import { ShieldIcon, ExpandAllIcon, CollapseAllIcon } from './headerIcons'

const normalizeServiceDisplayName = (name) => {
  if (!name) return ''
  const rawName = String(name).trim()
  if (!rawName) return ''
  const stripped = rawName
    .replace(/\s*[-–]?\s*\(?(oem|aftermarket)\)?\s*$/i, '')
    .trim()
  return stripped || rawName
}

// Diagnostic scans carry an OEM/Aftermarket choice. normalizeServiceDisplayName
// strips any trailing "(OEM)/(Aftermarket)" from the name, so re-derive the scan
// type — prefer the explicit svc.scanType, else recover it from the raw operation
// name — and re-append it consistently as " (OEM)" / " (Aftermarket)".
const scanTypeSuffix = (svc) => {
  if (!svc) return ''
  let type = svc.scanType
  if (!type && svc.operation && svc.operation.name) {
    const match = String(svc.operation.name).match(/\(?(oem|aftermarket)\)?\s*$/i)
    if (match) type = match[1]
  }
  const normalized = String(type || '').toLowerCase()
  if (normalized === 'oem') return ' (OEM)'
  if (normalized === 'aftermarket') return ' (Aftermarket)'
  return ''
}

const AdasSections = ({ report, allowViewPricing, expandAll, expandNonce, onExpandAll }) => {
  const [showSafety, setShowSafety] = useState(false)
  // Which sections are expanded, keyed by sensor name. This used to live inside
  // each SensorInsights, but the section header — which now pins only while its
  // own section is open — is a SIBLING of that component and could not see it.
  // Same shape as the admin portal's `expandedSensors` so the two match.
  const [openBySensor, setOpenBySensor] = useState({})
  const didSeed = useRef(false)
  const services = (report.adas && report.adas.services) || []

  const sensorInsights = report.sensorInsights || []
  // Use the same collision flag as the admin portal: the editable, top-level
  // report field (admin toggles it via vehicleInvolvedInCollision), NOT the
  // VIN-decode hint under adas.vinDecodeData — the two can diverge.
  const collision = !!report.vehicleInvolvedInCollision
  const make = ((report.vehicle && report.vehicle.make) || '').toLowerCase()
  const collisionPhrase = make === 'ford' ? 'an accident' : 'a collision'

  // Group by categoryName, then by sensorName — matching admin's filter:
  // hide not-equipped sensors, and hide collision-only services when the
  // vehicle was not in a collision.
  //
  // Built above the empty-state early return below so the expansion effects can
  // depend on the resulting group list — hooks must not sit after a return.
  const categories = {}
  services.forEach(svc => {
    if (svc.recommendedStatus === 'not-equipped') return
    if (svc.collisionRelated && !collision) return
    const cat = svc.categoryName || 'Uncategorized'
    if (!categories[cat]) categories[cat] = {}
    const sensor = svc.sensorName || 'General'
    if (!categories[cat][sensor]) categories[cat][sensor] = { services: [] }
    categories[cat][sensor].services.push(svc)
  })

  // Flat list of every rendered group, in order — the same derivation as admin's
  // getAllSensorNames. Joined into a string so the effects below have a stable
  // primitive dependency; `categories` is rebuilt on every render.
  const sensorNames = Object.values(categories).reduce(
    (names, sensors) => names.concat(Object.keys(sensors)),
    []
  )
  const sensorKey = sensorNames.join('|')

  // Seed once, when the report's services first land (#37249, "expand all by
  // default"). A ref latch rather than derived state: once the user has
  // collapsed something we must not re-expand it behind them. Mirrors admin's
  // didSeedExpansion.
  useEffect(() => {
    if (didSeed.current || !sensorKey) return
    didSeed.current = true
    const allOpen = {}
    sensorKey.split('|').forEach(name => { allOpen[name] = true })
    setOpenBySensor(allOpen)
  }, [sensorKey])

  // Re-apply the parent's Expand/Collapse All broadcast. Keyed on the nonce so
  // pressing the same button twice still re-applies it. Deliberately does NOT
  // depend on sensorKey — the seed above owns "the groups changed"; this fires
  // on a button press only.
  useEffect(() => {
    if (!sensorKey) return
    const next = {}
    sensorKey.split('|').forEach(name => { next[name] = !!expandAll })
    setOpenBySensor(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAll, expandNonce])

  const toggleSensor = (sensorName, next) =>
    setOpenBySensor(prev => ({ ...prev, [sensorName]: next }))

  // Figma 18796:156102 / :156106 give the expand/collapse buttons an ACTIVE
  // state, but they are commands rather than a toggle pair, so there is no
  // stored "mode" to read. Derive it: all open, all closed, or neither
  // highlighted when the user has a mixed selection.
  const openCount = sensorNames.filter(name => openBySensor[name]).length
  let expansion = 'mixed'
  if (sensorNames.length) {
    if (openCount === sensorNames.length) expansion = 'all'
    else if (openCount === 0) expansion = 'collapsed'
  }

  if (!services.length) {
    return <p style={{ color: '#69787F', marginTop: 24 }}>No ADAS services on this report.</p>
  }

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>ADAS Report</h3>
          <label className='adas-detail__collision-check'>
            <input type='checkbox' checked={!!collision} readOnly />
            <span>Vehicle was involved in {collisionPhrase}</span>
          </label>
        </div>
        <div className='adas-detail__report-header-right'>
          <button className='adas-detail__btn-safety' onClick={() => setShowSafety(true)}>
            <ShieldIcon />
            Safety Systems
          </button>
          <span className='adas-detail__header-divider' aria-hidden='true' />
          {/* Expand/collapse all surfaced as icon buttons rather than buried in
              the kebab (#37249), matching the admin portal. The kebab held
              nothing else here, so it is removed rather than left with one
              item. The handlers live in the parent so they also reach the
              Mechanical card, which is a sibling of this one. */}
          <div className='adas-detail__insight-toggles'>
            <button
              type='button'
              className={`adas-detail__insight-toggle${expansion === 'all' ? ' adas-detail__insight-toggle--active' : ''}`}
              onClick={() => onExpandAll(true)}
              title='Expand all insights'
              aria-label='Expand all insights'
              aria-pressed={expansion === 'all'}
            >
              <ExpandAllIcon />
            </button>
            <button
              type='button'
              className={`adas-detail__insight-toggle${expansion === 'collapsed' ? ' adas-detail__insight-toggle--active' : ''}`}
              onClick={() => onExpandAll(false)}
              title='Collapse all insights'
              aria-label='Collapse all insights'
              aria-pressed={expansion === 'collapsed'}
            >
              <CollapseAllIcon />
            </button>
          </div>
        </div>
      </div>

      <div>
        {Object.entries(categories).map(([catName, sensors]) => (
          <div key={catName}>
            {Object.entries(sensors).map(([sensorName, { services: sensorServices }]) => (
              // The wrapper is the sticky containing block for the header below —
              // it needs the class so the header is pushed out exactly as the
              // next group's header arrives. Matches admin's __sensor-group.
              <div key={sensorName} className='adas-detail__sensor-group'>
                <div
                  className={`adas-detail__group-header${openBySensor[sensorName] ? ' adas-detail__group-header--sticky' : ''}`}
                >
                  <span className='adas-detail__group-category'>{catName}</span>
                  <span className='adas-detail__group-sensor'>{displaySensorName(sensorName)}</span>
                </div>

                {sensorServices.map(svc => {
                  const baseName = normalizeServiceDisplayName(svc.operation ? svc.operation.name : '')
                  const serviceName = baseName ? baseName + scanTypeSuffix(svc) : '—'
                  return (
                  <div key={svc._id} className='adas-detail__service-row'>
                    <div className='adas-detail__service-name'>
                      <span>{serviceName}</span>
                      {svc.calibrationType && (
                        <span className='calibration-type'>({svc.calibrationType})</span>
                      )}
                    </div>
                    <div className='adas-detail__service-status'>
                      <span className='adas-detail__meta-label'>Status:</span>
                      <StatusBadge status={svc.recommendedStatus} source={svc.recommendedSource} />
                    </div>
                    {allowViewPricing && (
                      <div className='adas-detail__service-price'>
                        <span className='adas-detail__meta-label'>Price:</span>
                        <span className='adas-detail__price-value'>{formatPrice(svc.amount)}</span>
                      </div>
                    )}
                    <span className='adas-detail__service-more'>⋯</span>
                  </div>
                  )
                })}

                <SensorInsights
                  sensorName={sensorName}
                  services={sensorServices}
                  sensorInsights={sensorInsights}
                  report={report}
                  collision={!!collision}
                  open={!!openBySensor[sensorName]}
                  onToggle={next => toggleSensor(sensorName, next)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <SafetySystemsModal show={showSafety} onHide={() => setShowSafety(false)} report={report} />
    </div>
  )
}

export default AdasSections
